import { cloneDeep, debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton, Typography } from "@mui/material";
import { generatePath, useNavigate } from "react-router-dom";
import TechpackService from "../../service/TechpackService";
import { ROUTES } from "../../constant/common.constant";
import TechpackArtwork from "./TechpackArtwork/TechpackArtwork";
import TechpackCustomColor from "./TechpackCustomColor";
import styles from "./techpackItem.module.scss";
import {
  Checkbox,
  FlexColBox,
  Button,
  Box,
  Label,
  Link,
  EditableSelectInput,
  GradientChip,
  CopyButton,
} from "../../common/components";
import {
  ProductLineType,
  TechpackItem as TechpackItemType,
  TechpackItemQuantity,
  DesignPartElementStyleLayer,
} from "../../types";
import { palette } from "../../styles/theme";
import ColorBox from "../../common/components/Select/ColorSelect/ColorBox/ColorBox";
import { EditableTextInput } from "../../common/components/Input/EditableTextInput";
import { useForm } from "../../common/hooks/useForm";
import { translateSpecs } from "../../utils/specsTranslation";
import TechpackItemSize from "./TechpackItemSize";
import { PriceOption } from "../../types";

interface Props {
  isProjectStillExists: Promise<boolean>;
  techpackId: string;
  techpackItem: TechpackItemType;
  techpackItemIndex: number;
  techpackitemsCount: number;
  orderType: string;
  orderId: string;
}

const TechpackItem = ({
  techpackId,
  techpackItem,
  techpackItemIndex,
  techpackitemsCount,
  orderType,
  orderId,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isProjectStillExists, setIsProjectStillExists] = useState<
    boolean | null
  >(null);
  const [isProjectStillExistsLoading, setIsProjectStillExistsLoading] =
    useState(true);

  const formik = useForm({
    initialValues: {
      sku: techpackItem.sku,
      version: techpackItem.version,
      priceOption: techpackItem.priceOption || PriceOption.REGULAR_PRICE,
      differentQuantity: techpackItem.differentQuantity,
      customPrice: techpackItem.customPrice,
      putSkuOnProduct: techpackItem.putSkuOnProduct || false,
      cardboardSleeves: techpackItem.cardboardSleeves || false,
      individualPolyBags: techpackItem.individualPolyBags || false,
      customNeckTags: techpackItem.customNeckTags || false,
      hangTags: techpackItem.hangTags || false,
    },
    onSubmit: (values) => {},
  });

  const [sizesQuantities, setSizesQuantities] = useState<
    TechpackItemQuantity[] | null
  >(null);

  useEffect(() => {
    setIsProjectStillExistsLoading(true);
    props.isProjectStillExists.then((exists) => {
      setIsProjectStillExists(exists);
      setIsProjectStillExistsLoading(false);
    });
  }, [props.isProjectStillExists]);

  const isHyperCustom =
    techpackItem.designState.productLine === ProductLineType.HYPER_CUSTOM;

  const decorationDefinitions = techpackItem.decorationDefinitions;

  const customColors = techpackItem.techpackitemcustomcolors;

  const images = techpackItem.designState.images;

  const designName = techpackItem?.purchaseddesign?.originalDesignName;
  const projectName =
    techpackItem?.purchaseddesign?.originalProjectName || "Unknown";
  const teamId = techpackItem?.purchaseddesign?.originalTeamId;
  const projectId = techpackItem?.purchaseddesign?.originalProjectId;

  const designWithColors = techpackItem.designState.designWithColors;

  //For initial setting of size quantities
  useEffect(() => {
    setSizesQuantities(techpackItem.techpackitemquantities);
  }, [techpackItem.techpackitemquantities]);

  const techpackItemArtworks = techpackItem.techpackitemartworks;

  /**
   *
   */
  const onVersionChange = useCallback(
    async (value: string) => {
      const newVersion = value || ""; //Null should be ""

      const data = {
        techpackId: techpackId,
        techpackItemId: techpackItem.id,
        version: newVersion,
      };
      await TechpackService.setVersion(data);
      formik.setFieldValue("version", newVersion);
    },
    [techpackId, techpackItem.id, formik]
  );

  // const onSkuChange = useCallback(
  //   async (value: string) => {
  //     const newSku = value || ""; //Null should be ""
  //     const data = {
  //       techpackId: techpackId,
  //       techpackItemId: techpackItem.id,
  //       sku: newSku,
  //     };
  //     await TechpackService.setSku(data);
  //     formik.setFieldValue("sku", newSku);
  //   },
  //   [techpackId, techpackItem.id, formik]
  // );

  const onQtyChange = useCallback(
    async (sizeField: string, sizeName: string, sizeQty: string) => {
      const saveSizeQty = debounce(
        async (field, name, qty) => {
          const data = {
            techpackId: techpackId,
            techpackItemId: techpackItem.id,
            sizes: [
              {
                field: field,
                sizeName: name,
                qty: qty,
              },
            ],
          };

          await TechpackService.setSize(data);
        },
        100,
        { leading: true }
      );

      await saveSizeQty(sizeField, sizeName, sizeQty);

      //Updating local state now that the API call is done (faster than reload the full techpack...)
      setSizesQuantities((oldSizes) => {
        const newSizes = cloneDeep(oldSizes);
        const sizeToUpdate = newSizes?.find(
          (size) => size.sizeName === sizeName
        );
        if (!sizeToUpdate) {
          return oldSizes;
        }
        const newSizeQty = Number(sizeQty);

        switch (sizeField) {
          case "ORDER": {
            sizeToUpdate.orderQuantity = newSizeQty;
            break;
          }
          case "OVER": {
            sizeToUpdate.overQuantity = newSizeQty;
            break;
          }
          case "QC": {
            sizeToUpdate.qcQuantity = newSizeQty;
            break;
          }
          default: {
            //Nothing to do
          }
        }

        return newSizes;
      });
    },
    [techpackId, techpackItem.id]
  );

  const onClickEdit = useCallback(() => {
    navigate(
      generatePath(ROUTES.ADMIN_STUDIO_PURCHASED_DESIGN, {
        designId: techpackItem.designState.designId,
        orderType: orderType,
        orderId: orderId,
        historyFrom: "techpack",
      })
    );
  }, [navigate, techpackItem.designState.designId, orderType, orderId]);

  const onAdditionalOptionsFieldChange = useCallback(
    (field: string) => async (e: React.ChangeEvent<HTMLInputElement>) => {
      formik.setFieldValue(field, e.target.checked);

      const data = {
        techpackId: techpackId,
        techpackItemId: techpackItem.id,
        [field]: e.target.checked,
      };
      await TechpackService.updateAdditionalOptionsField(data, field);
    },
    [techpackId, techpackItem.id, formik]
  );

  const onPriceOptionChange = useCallback(
    async (
      priceOption: PriceOption,
      customPrice?: number,
      differentQuantity?: number
    ) => {
      if (priceOption === PriceOption.CUSTOM_PRICE) {
        formik.setFieldValue("customPrice", customPrice);
      } else if (priceOption === PriceOption.DIFFERENT_QTY) {
        formik.setFieldValue("differentQuantity", differentQuantity);
      }
      formik.setFieldValue("priceOption", priceOption);
      const data = {
        techpackId: techpackId,
        techpackItemId: techpackItem.id,
        priceOption: priceOption,
        customPrice: customPrice,
        differentQuantity: differentQuantity,
      };
      await TechpackService.setPriceOption(data);
    },
    [techpackId, techpackItem.id, formik]
  );

  const getColorLabel = (layer: DesignPartElementStyleLayer) => {
    if (layer.colorLabel) {
      return `${t(`colors.${layer.colorLabel}`)} (${layer.colorName})`;
    }
    return t("techpack.custom_color");
  };

  return (
    <Box display={"flex"} flexDirection={"column"} gap={"12px"}>
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
        fullWidth
      >
        <Box display={"flex"} gap={"8px"} alignItems={"center"}>
          <Typography variant="h1">
            {`${designName} (${techpackItem.factoryInfo})`}
          </Typography>
          {techpackItem.designState.customizationPricing.creditsUsedInDesign !=
          null ? (
            <GradientChip
              sx={{
                "&>span": {
                  fontSize: "12px",
                },
              }}
            >
              {t("general.credits", {
                count:
                  techpackItem.designState.customizationPricing
                    .creditsUsedInDesign,
              })}
            </GradientChip>
          ) : (
            <Skeleton variant="text" width={"75px"} height={"2px"} />
          )}
        </Box>
        <Button variant="outlined" size="small" onClick={onClickEdit}>
          {t("techpack.edit_design")}
        </Button>
      </Box>

      <Box
        display={"flex"}
        sx={{
          backgroundColor: palette.white,
          borderRadius: "8px",
          p: "12px",
          border: "1px solid",
          borderColor: palette.grey.lightGrey,
        }}
      >
        <FlexColBox width={"50%"}>
          <Typography mb={"8px"} variant="textRegularBold">
            {t("general.info")}
          </Typography>
          <Box
            display={"flex"}
            gap={"8px"}
            alignItems={"center"}
            height={"34px"}
          >
            <Label sx={{ whiteSpace: "nowrap" }}>{t("techpack.project")}</Label>
            {isProjectStillExistsLoading ? (
              <Skeleton variant="text" width={"100px"} height={"20px"} />
            ) : isProjectStillExists ? (
              <Link
                to={generatePath(ROUTES.PROJECT, {
                  teamId: teamId,
                  projectId: projectId,
                })}
                target="_blank"
                endIcon={"arrow_outward"}
              >
                {projectName}
              </Link>
            ) : (
              <Typography variant="textRegularBold">
                {projectName}
                <Typography variant="textSm" color={palette.grey.primary}>
                  {t("techpack.project_not_found")}
                </Typography>
              </Typography>
            )}
          </Box>
          {/* will be uncommented in future release */}
          {/* <EditableTextInput
            name="sku"
            defaultValue={formik.values.sku}
            onConfirm={onSkuChange}
            label={t("techpack.sku")}
          /> */}
          <EditableTextInput
            name="version"
            defaultValue={formik.values.version}
            onConfirm={onVersionChange}
            label={t("techpack.version")}
          />
          <EditableSelectInput
            defaultValue={formik.values.priceOption}
            defaultInputValue={
              formik.values.priceOption === PriceOption.CUSTOM_PRICE
                ? formik.values.customPrice?.toString()
                : formik.values.priceOption === PriceOption.DIFFERENT_QTY
                  ? formik.values.differentQuantity?.toString()
                  : undefined
            }
            label={t("general.price")}
            name="priceOption"
            data-test="priceOption"
            options={[
              {
                label: t("techpack.price_options.regular_price"),
                value: PriceOption.REGULAR_PRICE,
              },
              {
                label: t("techpack.price_options.free_defect_replacement"),
                value: PriceOption.FREE_DEFECT_REPLACEMENT,
              },
              {
                label: t("techpack.price_options.custom_price"),
                value: PriceOption.CUSTOM_PRICE,
                withInput: true,
                unitInput: "money",
              },
              {
                label: t("techpack.price_options.pre_production_sample"),
                value: PriceOption.PRE_PRODUCTION_SAMPLE,
              },
              {
                label: t("techpack.price_options.different_qty"),
                value: PriceOption.DIFFERENT_QTY,
                withInput: true,
              },
            ]}
            onConfirm={(value, inputValue) =>
              onPriceOptionChange(
                value as PriceOption,
                Number(inputValue),
                Number(inputValue)
              )
            }
          />
        </FlexColBox>
        <FlexColBox gap={"8px"} width={"50%"}>
          <Typography variant="textRegularBold">
            {t("techpack.additional_options")}
          </Typography>
          <Checkbox
            label={t("techpack.put_sku_on_product")}
            checked={formik.values.putSkuOnProduct}
            onChange={(e, checked) => {
              onAdditionalOptionsFieldChange("putSkuOnProduct")(e);
              if (checked) {
                onAdditionalOptionsFieldChange("hangTags")(e);
              }
            }}
            labelVariant="textRegular"
          />
          {techpackItem.purchaseddesign.factoryId === "RI" && (
            <>
              <Checkbox
                label={t("techpack.hang_tags")}
                checked={formik.values.hangTags}
                onChange={(e, checked) => {
                  onAdditionalOptionsFieldChange("hangTags")(e);
                  if (!checked) {
                    onAdditionalOptionsFieldChange("putSkuOnProduct")(e);
                  }
                }}
                labelVariant="textRegular"
              />
              <Checkbox
                label={t("techpack.individual_poly_bags")}
                checked={formik.values.individualPolyBags}
                onChange={onAdditionalOptionsFieldChange("individualPolyBags")}
                labelVariant="textRegular"
              />
            </>
          )}
          {/* <Checkbox
            label={t("techpack.custom_neck_tags")}
            checked={formik.values.customNeckTags}
            onChange={onPutSkuOnProductChange}
          /> */}
          <Box display={"flex"} gap={"8px"} alignItems={"center"}></Box>
        </FlexColBox>
      </Box>
      <Box display={"flex"} gap={"12px"}>
        <Box
          sx={{
            p: "24px",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: palette.grey.lightGrey,
            background: palette.white,
            display: "flex",
            gap: "24px",
            width: "100%",
            maxWidth: "65%",
            "& img": {
              width: "calc(50% - 12px)",
              height: "auto",
              maxHeight: "500px",
              objectFit: "contain",
            },
          }}
        >
          {images?.map((image) => (
            <img
              src={image.url}
              alt={image.side}
              className={styles.img}
              key={image.side}
            />
          ))}
        </Box>

        <Box
          display={"flex"}
          flexDirection={"column"}
          sx={{
            width: "35%",
            p: "24px",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: palette.grey.lightGrey,
            background: palette.white,
          }}
          gap={"32px"}
        >
          {designWithColors?.parts?.map((part) => (
            <Box key={part.name} display={"flex"} flexDirection={"column"}>
              <Typography variant="textRegularBold" mb={"12px"}>
                {translateSpecs(
                  `specs.${designWithColors.product.name}.${part.name}`
                )}
              </Typography>
              <Box display={"flex"} flexDirection={"column"} gap={"12px"}>
                {part.elements.map(
                  (element) =>
                    element.active && (
                      <Box
                        key={element.name}
                        display={"flex"}
                        flexDirection={"column"}
                        gap={"4px"}
                      >
                        <Typography variant="textSm">
                          {translateSpecs(
                            `specs.${designWithColors.product.name}.${element.name}`
                          )}
                        </Typography>
                        {element.style.layers.length > 0 ? (
                          <Box
                            display={"flex"}
                            flexDirection={"column"}
                            gap={"4px"}
                          >
                            {element.style.layers.map(
                              (layer) =>
                                layer.active && (
                                  <React.Fragment key={layer.name}>
                                    <Typography
                                      variant="textSm"
                                      color={palette.grey.primary}
                                      display={"flex"}
                                      alignItems={"center"}
                                      gap={"4px"}
                                    >
                                      {`${translateSpecs(`specs.${designWithColors.product.name}.${layer.name}`)}: `}
                                      <Box
                                        component={"span"}
                                        display={"inline-flex"}
                                        alignItems={"center"}
                                        gap={"2px"}
                                        flexWrap={"nowrap"}
                                      >
                                        {`${getColorLabel(layer)}`}
                                        <ColorBox
                                          colorOrCombo={
                                            layer.hex || layer.colorName
                                          }
                                          size={14}
                                          sx={{ cursor: "default", ml: "8px" }}
                                        />
                                        {layer.hex || layer.colorName}
                                      </Box>
                                      <CopyButton
                                        value={
                                          (layer.hex || layer.colorName)?.split(
                                            "#"
                                          )[1]
                                        }
                                      />
                                    </Typography>
                                  </React.Fragment>
                                )
                            )}
                          </Box>
                        ) : (
                          <Typography
                            variant="textSm"
                            color={palette.grey.primary}
                            pl={"8px"}
                          >
                            {translateSpecs(
                              `specs.${designWithColors.product.name}.${element.style.name}`
                            )}
                          </Typography>
                        )}
                      </Box>
                    )
                )}
              </Box>
            </Box>
          ))}
          {customColors && customColors.length > 0 && (
            <TechpackCustomColor customColors={customColors} />
          )}
        </Box>
      </Box>

      <TechpackItemSize
        sizesQuantities={sizesQuantities || []}
        isHyperCustom={isHyperCustom}
        onQtyChange={onQtyChange}
      />

      <FlexColBox gap={"12px"}>
        {/* <span className={styles.artworkTitle}>{t("specs.decorations")}</span> */}
        {techpackItemArtworks
          .filter((techpackItemArtwork) => {
            const decoration = techpackItemArtwork.decoration;
            return (
              decoration?.assetId && decoration.active && !decoration.inError
            );
          })
          .map(
            (
              techpackItemArtwork,
              techpackItemArtworkIndex,
              filteredTechpackItemArtworks
            ) => {
              const decoration = techpackItemArtwork.decoration;
              let decorationDefinition = decorationDefinitions?.find(
                (d) => d.id === decoration.type
              );
              if (!decorationDefinition) {
                decorationDefinition = decorationDefinitions?.find((d) =>
                  d.legacyAssetTypes?.includes(decoration.type)
                );
              }
              //const productionDecoration = techpackItemArtwork.production_decoration;

              return (
                <TechpackArtwork
                  key={techpackItemArtwork.id}
                  techpackId={techpackId}
                  techpackItemId={techpackItem.id}
                  techpackItemArtwork={techpackItemArtwork}
                  techpackItemArtworkIndex={techpackItemArtworkIndex}
                  techpackItemArtworkCount={filteredTechpackItemArtworks.length}
                  decoration={decoration}
                  decorationDefinition={decorationDefinition}
                />
              );
            }
          )}
      </FlexColBox>
    </Box>
  );
};

export default TechpackItem;
